import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import {
  Text,
  Badge,
  Heading,
  Donut,
  Button,
  // eslint-disable-next-line no-unused-vars
  ButtonCircle,
} from 'rebass';
import { Flex, Box } from 'reflexbox';

import config from 'frontend/config';

import SvgIcons from 'frontend/SvgIcons';
import {
  DarkerBox,
  If,
  ProgressBar,
  Responsive,
  SpecIcon,
  TinyButton,
} from 'frontend/components';
import actions from 'frontend/state/actions';
import { selectors as reportSelectors } from 'frontend/state/reducers/simbot/report';

import SharedConfig from 'shared/config';
import { humanTimeDisplay } from 'shared/time';

import SimcLog from './SimcLog';

const { colors } = config;

const JOB_STATES = {
  inactive: {
    text: 'In Queue',
    color: colors.info,
    theme: 'info',
  },
  active: {
    text: 'Processing',
    color: colors.blue,
    theme: 'secondary',
  },
  complete: {
    text: 'Complete',
    color: colors.success,
    theme: 'success',
  },
  failed: {
    text: 'Failed',
    color: colors.error,
    theme: 'error',
  },
  delayed: {
    text: 'Retrying',
    color: colors.info,
    theme: 'info',
  },
};

function JobStatus({
  title,
  simType,
  player,
  charClass,
  spec,
  fightStyle,
  fightLength,
  enemyCount,
  simcVersion,
  concurrency,
  state,
  log,
  queuePosition,
  queueSize,
  waitAvgPublic,
  waitAvgPatron,
  userConcurrency,
  isSwiftEligible,
  isPatron,
  slowMode,
  percentComplete,
  stageProgress,
  curStage,
  numStages,
  numActors,
  detailedProgress,
  isLargeScreen,
  onClickCancel,
  onClickDetailedProgress,
}) {
  const [freshPage, setFreshPage] = React.useState(true);
  React.useEffect(() => {
    setTimeout(() => {
      setFreshPage(false);
    }, 10 * 1000);
  });

  let stateToShow = state;
  // don't show Queued to user once processing has started
  if (percentComplete > 0 && state === 'inactive') {
    stateToShow = 'active';
  }
  // handle some new cases with Bull, probably stuck/paused
  if (!JOB_STATES[stateToShow]) {
    stateToShow = 'active';
  }
  const { color, text, theme } = JOB_STATES[stateToShow];
  let pill;
  if (stateToShow === 'inactive') {
    pill = <Badge pill theme={theme}>{queuePosition} / {queueSize}</Badge>;
  }
  let donutColor;
  if (percentComplete < 100) {
    donutColor = 'secondary';
  } else {
    donutColor = 'success';
  }

  const isSwift = concurrency > 1;

  const numActorsComplete = _.floor(numActors * stageProgress);
  let iconContainerHeight = 60;
  let iconContainerWidth = 900;
  if (!isLargeScreen) {
    iconContainerHeight = 100;
    iconContainerWidth = 300;
  }
  // estimate of icon size, may be too big due to last icon not fitting
  let calcIconSize = _.floor(Math.sqrt((iconContainerHeight * iconContainerWidth) / numActors)) + 1;

  // make sure we fit in the container height taking into account "lost" space
  let calcHeight;
  do {
    calcIconSize -= 1;
    const iconsPerRow = _.floor(iconContainerWidth / calcIconSize);
    calcHeight = calcIconSize * _.ceil(numActors / iconsPerRow);
  } while (calcHeight > iconContainerHeight);

  const iconSize = _.clamp(calcIconSize, 4, 32);
  const stageNames = [
    'Low Precision',
    'Medium Precision',
    'High Precision',
  ];

  const radiusAmount = 10;

  const subHeadParts = _.filter([
    _.startCase(fightStyle),
    fightLength && humanTimeDisplay(fightLength, { round: false }),
    enemyCount && `${enemyCount} target${enemyCount !== 1 ? 's' : ''}`,
    _.startCase(simcVersion),
  ]);

  return (
    <div>
      <DarkerBox style={{ border: isSwift ? `2px solid ${config.colors.orange}` : '' }}>
        <Flex justify="space-between" wrap>
          <Box mb={1}>
            <Heading level={3}>{title}</Heading>
            <If
              condition={simType !== 'advanced'}
              then={() => (
                <Flex align="center">
                  <Box style={{ marginRight: 5 }}>
                    <SpecIcon charClass={charClass} spec={spec} size={22} />
                  </Box>
                  <Text>
                    {_.startCase(player)} - {_.startCase(charClass)} - {_.startCase(spec)}
                  </Text>
                </Flex>
              )}
              else={() => (
                <Text>Advanced</Text>
              )}
            />

            <Text small>{_.join(subHeadParts, ' - ')}</Text>
          </Box>
          <If
            condition={!log}
            then={() => (
              <Box>
                <Text small bold>
                  Estimated wait time
                </Text>
                <Text small ml={1}>
                  Premium: {humanTimeDisplay(waitAvgPatron)}
                </Text>
                <Text small ml={1}>
                  Public: {humanTimeDisplay(waitAvgPublic)}
                </Text>
              </Box>
            )}
          />
        </Flex>

        <Flex justify="space-between" align="center" mt={2} mb={1} wrap>
          <Flex align="center">
            <Box>
              <Text bold>Job Status:</Text>
            </Box>
            <Box ml={1}>
              <Text color={color} bold>
                {text}
              </Text>
            </Box>
            <Box ml={1}>
              {pill}
            </Box>
          </Flex>
        </Flex>
        <If
          condition={!freshPage && isPatron && state === 'inactive' && queuePosition > 1}
          then={() => (
            <Box>
              <Text bold color={config.colors.orange} mt={2}>
                Hey! Why am I in the queue when I&apos;m Premium?
              </Text>
              <Text size={14} mt={1}>
                Raidbots is likely experiencing a temporary surge of traffic which should resolve shortly.
              </Text>
              <Text size={14} mt={1}>
                This is most common around the NA and EU
                {' '}
                <a
                  href="https://wowreset.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  weekly reset
                </a>
                {' '}
                since a ton of folks are simming their Vault.
              </Text>
              <Text size={14} mt={1}>
                Your sim will be processed before any non-Premium sims but there are other Premium sims that were
                submitted before yours.
              </Text>
              <Text size={14} mt={1}>
                If your sim doesn&apos;t start within a minute or two, check Raidbots Discord for any
                service announcements.
              </Text>
            </Box>
          )}
        />
        <If
          condition={slowMode}
          then={() => (
            <Text color={config.colors.warning} my={1}>
              Site is under heavy load so progress updates have been slowed down
            </Text>
          )}
        />

        <If
          condition={detailedProgress && numStages > 1}
          then={() => (
            <Flex
              align="center"
              style={{
                borderRadius: radiusAmount,
                backgroundColor: '#222',
              }}
              mb={1}
            >
              {_.map(_.range(0, numStages), stage => {
                const isCurrentStage = stage === curStage;

                let borderRadius;
                let backgroundColor = 'transparent';
                if (stage < curStage) {
                  borderRadius = [0, 0, 0, 0];
                  backgroundColor = 'rgba(0, 217, 247, 0.75)';
                } else if (stage === curStage) {
                  borderRadius = [0, radiusAmount, radiusAmount, 0];
                  backgroundColor = config.colors.blue;
                }
                if (stage === 0) {
                  borderRadius[0] = radiusAmount;
                  borderRadius[3] = radiusAmount;
                }
                if (stage === numStages) {
                  borderRadius[1] = radiusAmount;
                  borderRadius[2] = radiusAmount;
                }

                return (
                  <Box
                    key={`stage-${stage}`}
                    auto
                    style={{
                      borderRadius: _.join(_.map(borderRadius, br => `${br}px`), ' '),
                      backgroundColor,
                      lineHeight: 1.2,
                    }}
                  >
                    <Text
                      align="center"
                      bold={isCurrentStage}
                      color={stage <= curStage ? config.colors.black : config.colors.midgray}
                      size={16}
                    >
                      Stage {stage + 1}
                    </Text>
                    <Text
                      align="center"
                      bold={isCurrentStage}
                      small
                      color={stage <= curStage ? config.colors.black : config.colors.midgray}
                    >
                      {stageNames[stage]}
                      {stage < curStage && isLargeScreen && ': Complete'}
                    </Text>
                  </Box>
                );
              })}
            </Flex>
          )}
        />
        <If
          condition={log}
          then={() => (
            <Box>
              <Flex align="flex-start" justify="center" wrap>
                <Box sm={12} md={2} lg={2} mt={2} mr={2}>
                  <Donut
                    color={donutColor}
                    size={180}
                    strokeWidth={15}
                    value={_.min([detailedProgress ? stageProgress : percentComplete / 100, 1])}
                    mr={1}
                  />
                </Box>
                <Box auto style={{ maxWidth: iconContainerWidth }}>
                  <SimcLog log={log} />
                  <If
                    condition={!detailedProgress && numStages > 1}
                    then={() => (
                      <Flex align="center" style={{ lineHeight: 1.1, marginTop: 4 }} my={0}>
                        <Text bold small>
                          Smart Sim: Stage {curStage + 1}
                        </Text>
                        <Box auto ml={1}>
                          <ProgressBar
                            pct={100 * stageProgress}
                            completeColor="#666"
                            barText=""
                            barTextColor={config.colors.white}
                          />
                        </Box>
                      </Flex>
                    )}
                  />

                  <If
                    condition={detailedProgress && numStages > 1}
                    then={() => (
                      <>
                        <Flex
                          wrap
                          style={{
                            width: iconContainerWidth,
                            marginTop: 4,
                            marginRight: -2,
                          }}
                        >
                          {_.map(_.range(0, numActors), i => {
                            const complete = i < numActorsComplete || numActors === numActorsComplete;
                            if (iconSize < 14) {
                              const margin = _.round(iconSize / 2.5);
                              return (
                                <span
                                  key={i}
                                  style={{
                                    borderRadius: '50%',
                                    backgroundColor: complete ? config.colors.secondary : config.colors.primary,
                                    height: iconSize - margin,
                                    width: iconSize - margin,
                                    marginTop: margin,
                                    marginRight: margin,
                                    display: 'inline-block',
                                  }}
                                />
                              );
                            }
                            return (
                              <SvgIcons.gear.small
                                key={i}
                                style={{
                                  fill: complete ? config.colors.secondary : config.colors.primary,
                                  width: iconSize,
                                  height: iconSize,
                                  paddingTop: 1,
                                  paddingRight: 1,
                                }}
                              />
                            );
                          })}
                        </Flex>
                        <Text small mt={0}>
                          {_.floor(stageProgress * numActors).toLocaleString()}
                          {' '}
                          of
                          {' '}
                          {numActors.toLocaleString()} combinations complete
                        </Text>
                      </>
                    )}
                  />
                  <Flex style={{ marginBottom: 4 }}>
                    <If
                      condition={!detailedProgress}
                      then={() => (
                        <Box m={0}>
                          <TinyButton
                            theme="secondary"
                            p={1}
                            onClick={onClickDetailedProgress}
                          >
                            Switch to Detailed Mode
                          </TinyButton>
                        </Box>
                      )}
                      else={() => (
                        <Box m={0}>
                          <TinyButton
                            theme="primary"
                            p={1}
                            onClick={onClickDetailedProgress}
                          >
                            Switch to Simple Mode
                          </TinyButton>
                        </Box>
                      )}
                    />
                  </Flex>

                  <If
                    condition={isSwift}
                    then={() => (
                      <>
                        <Flex align="center" style={{ lineHeight: 1 }} mt={2}>
                          <Box mr={1}>
                            <SvgIcons.quick.small
                              style={{
                                fill: config.colors.orange,
                                width: 24,
                                height: 24,
                              }}
                            />
                          </Box>
                          <Text
                            bold
                            size={18}
                            color={config.colors.orange}
                            my={1}
                            mr={1}
                          >
                            SwiftSim:
                          </Text>
                          <Text
                            bold
                            size={18}
                            color={config.colors.orange}
                            my={1}
                          >
                            Your sim is running at {concurrency}x power
                            ({concurrency * SharedConfig.worker.numCores} {' '} dedicated CPU cores)
                          </Text>
                        </Flex>
                        <Text small>
                          Your Premium membership level gives you access to more power!
                          {' '}
                          <a
                            href="https://support.raidbots.com/article/63-swift-sim"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            More info
                          </a>
                        </Text>
                      </>
                    )}
                    else={() => (
                      <>
                        <Text
                          my={1}
                        >
                          Your sim is running at {concurrency}x power
                          ({concurrency * SharedConfig.worker.numCores} {' '} dedicated CPU cores)
                        </Text>
                        <If
                          condition={isSwiftEligible}
                          then={() => (
                            <Text small>
                              Want more power for your sims?
                              {' '}
                              <a
                                href="https://support.raidbots.com/article/63-swift-sim"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Learn about SwiftSim
                              </a>
                            </Text>
                          )}
                        />
                        <If
                          condition={userConcurrency > 1 && !isSwiftEligible}
                          then={() => {
                            let reason;
                            const supportedSimTypes = ['optimize', 'droptimizer'];
                            if (simType === 'advanced') {
                              reason = 'sim is not using profilesets or has too few profilesets';
                            } else if (_.includes(supportedSimTypes, simType)) {
                              reason = 'sim is too small';
                            } else {
                              reason = 'this type of sim is not supported';
                            }
                            return (
                              <Text small>
                                This sim is not eligible for SwiftSim ({reason})
                              </Text>
                            );
                          }}
                        />
                      </>
                    )}
                  />
                </Box>
                {/*
                <Flex justify="flex-start">
                  <Box style={{ backgroundColor: 'rgb(51, 100, 82)' }} p={1}>
                    LIGHT SPEED
                  </Box>
                  <Box ml={1} style={{ backgroundColor: 'rgb(167, 91, 47)' }} p={1}>
                    RIDICULOUS SPEED
                  </Box>
                  <Box ml={1} style={{ color: 'rgb(128, 43, 34)' }} p={1}>
                    LUDICROUS SPEED
                  </Box>
                </Flex>
                */}
              </Flex>
              <Flex justify="flex-end">
                <Box>
                  <Button
                    theme="primary"
                    onClick={onClickCancel}
                  >
                    Cancel Sim
                  </Button>
                </Box>
              </Flex>
            </Box>
          )}
        />
      </DarkerBox>

      <If
        condition={state === 'failed'}
        then={() => (
          <Box mb={2}>
            <Heading level={3}>Common Failure Reasons</Heading>

            <Text bold mt={2}>Simulations that take too long are killed.</Text>
            <Text>
              Try lowering your iterations, comparing fewer items, or using a simpler fight type
            </Text>

            <Text bold mt={2}>
              Try switching between Stable/Nightly version of SimC in the
              advanced options.
            </Text>
            <Text>
              If a new patch just landed, new items may not
              work in Stable. Nightly can also have bugs as it changes often.
            </Text>

            <Text bold mt={2}>
              You found a bug in Raidbots or SimulationCraft!
            </Text>
            <Text>
              Software is hard! Report the issue and/or contact Seri, check the
              footer for links. Please include a link to the simulation that
              failed.
            </Text>
          </Box>
        )}
      />

      <If
        condition={!isPatron}
        then={() => (
          <Box mb={2} mt={4}>
            <Heading level={3}>Get Raidbots Premium!</Heading>
            <ul>
              <li>Skip the line</li>
              <li>Larger Top Gear sims</li>
              <li>Premium Droptimizer features</li>
              <li>Faster sims with SwiftSim</li>
              <li>Guild skip-the-line for the Discord bot</li>
              <li>Keep the site ad-free</li>
              <li>Fund ongoing development of the site</li>
            </ul>
            <a
              href="/account"
              target="_blank"
            >
              Sign up on your account page
            </a>
          </Box>
        )}
      />
    </div>
  );
}

JobStatus.propTypes = {
  title: PropTypes.string.isRequired,
  simType: PropTypes.string.isRequired,
  player: PropTypes.string,
  charClass: PropTypes.string,
  spec: PropTypes.string,
  fightStyle: PropTypes.string.isRequired,
  fightLength: PropTypes.number.isRequired,
  enemyCount: PropTypes.number.isRequired,
  simcVersion: PropTypes.string.isRequired,
  concurrency: PropTypes.number.isRequired,
  isSwiftEligible: PropTypes.bool.isRequired,
  state: PropTypes.string.isRequired,
  log: PropTypes.string.isRequired,
  queueSize: PropTypes.number.isRequired,
  queuePosition: PropTypes.number.isRequired,
  waitAvgPublic: PropTypes.number,
  waitAvgPatron: PropTypes.number,
  isPatron: PropTypes.bool.isRequired,
  userConcurrency: PropTypes.number.isRequired,
  stageProgress: PropTypes.number,
  curStage: PropTypes.number,
  numStages: PropTypes.number,
  numActors: PropTypes.number,
  slowMode: PropTypes.bool.isRequired,
  percentComplete: PropTypes.number.isRequired,
  detailedProgress: PropTypes.bool.isRequired,
  isLargeScreen: PropTypes.bool.isRequired,
  onClickCancel: PropTypes.func.isRequired,
  onClickDetailedProgress: PropTypes.func.isRequired,
};

JobStatus.defaultProps = {
  player: null,
  charClass: null,
  spec: null,
  waitAvgPublic: null,
  waitAvgPatron: null,
  stageProgress: 0,
  curStage: 1,
  numStages: 1,
  numActors: 0,
};

const ResponsiveJobStatus = (props) => {
  return (
    <Responsive size="large">
      {(isLarge) => (
        <JobStatus
          {...props}
          isLargeScreen={isLarge}
        />
      )}
    </Responsive>
  );
};

const mapStateToProps = state => ({
  detailedProgress: reportSelectors.detailedProgress(state),
});

const mapDispatchToProps = {
  onClickDetailedProgress: actions.toggleDetailedProgress,
};

export default connect(mapStateToProps, mapDispatchToProps)(ResponsiveJobStatus);
